import {
  CostCategoryDisplay,
  costCategoryLabelTypes,
} from '@/types/thermostatCostCategoryControls'
import { useMemo } from 'react'
import { useAppDispatch } from '@/hooks'
import { thermostatCostCategoryControlsCollection } from '@/reducers/thermostatCostCategoryControls'
import { fetchAuthenticated } from '@/utils/fetch'
import { Result, ok, err } from 'neverthrow'
import * as yup from 'yup'
import { useNavigation } from '@/app/hooks'
import { getCostCategoryTypeFromRange } from '@/app/pages/thermostat/cost-categories/utils/costCategoryMappings'

export const updateCostCategoriesSchema = yup.object({
  lowPriceCost: yup
    .number()
    .typeError('Must be a number')
    .positive('Low cost hour must be greater than 0.00')
    .required()
    .test('less-than-high', (value, context) => {
      const highValue = context.parent?.highPriceCost
      if (highValue <= value) {
        return context.createError({
          message: `Low cost hour must be less than $${highValue.toFixed(2)}`,
        })
      }
      return true
    }),
  highPriceCost: yup
    .number()
    .typeError('Must be a number')
    .positive('High cost hour must be greater than 0.00')
    .required()
    .test('greater-than-low', (value, context) => {
      const lowValue = context.parent?.lowPriceCost
      if (lowValue >= value) {
        return context.createError({
          message: `High cost hour must be greater than $${lowValue.toFixed(
            2,
          )}`,
        })
      }
      return true
    }),
})

export type UpdateCostCategoriesFormData = yup.InferType<
  typeof updateCostCategoriesSchema
>

type CostCategoryControl = {
  id: number
  min_cost_cents: number | null
  max_cost_cents: number | null
  cool_setpoint?: number
  heat_setpoint?: number
}

type ActionSuccess = CostCategoryControl[]
type ActionError = {
  message: string
  status?: number
}

export default function useCostCategories(thermostatId: number) {
  const dispatch = useAppDispatch()
  const navigate = useNavigation()
  const {
    data: costCategoryControls,
    isLoadingOrIdle,
    refetch,
  } = thermostatCostCategoryControlsCollection.useFetch(
    `${thermostatId}/cost_category_controls`,
  )
  const costCategories = useMemo(
    () =>
      [...costCategoryControls]
        .sort(
          (a, b) =>
            (a.min_cost_cents ?? Number.NEGATIVE_INFINITY) -
            (b.min_cost_cents ?? Number.NEGATIVE_INFINITY),
        )
        .map((c, i) => {
          const type = getCostCategoryTypeFromRange({
            min_cost_cents: c.min_cost_cents,
            max_cost_cents: c.max_cost_cents,
          })

          // Adjust index for negative category presence
          const hasNegativeCategory = costCategoryControls.some(
            (cat) => cat.min_cost_cents === null,
          )
          const adjustedIndex = hasNegativeCategory ? i : i + 1

          return {
            id: c.id,
            minValue: c.min_cost_cents ?? Number.NEGATIVE_INFINITY,
            maxValue: c.max_cost_cents ?? Number.POSITIVE_INFINITY,
            type: costCategoryLabelTypes[adjustedIndex] || type,
            initValue: c.cool_setpoint_delta ?? 0,
          }
        }) satisfies CostCategoryDisplay[],
    [costCategoryControls],
  )
  const updateCostCategoryControls = async (
    thermostatId: number,
    costCategoryControls: CostCategoryControl[],
  ): Promise<Result<ActionSuccess, ActionError>> => {
    try {
      const response = await fetchAuthenticated(
        `/thermostats/${thermostatId}/cost_category_controls`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(costCategoryControls),
        },
      )

      if (!response.ok) {
        const errorText = await response.text()
        return err({
          message: errorText || 'Failed to update cost category controls',
          status: response.status,
        })
      }

      dispatch(thermostatCostCategoryControlsCollection.actions.invalidate())

      const data = await response.json()
      return ok(data)
    } catch (error) {
      return err({
        message:
          error instanceof Error ? error.message : 'Unknown error occurred',
      })
    }
  }
  const submitUpdateForm = async (formData: UpdateCostCategoriesFormData) => {
    const lowIndex = costCategoryControls.findIndex(
      (c) => c.min_cost_cents === 0,
    )
    const highIndex = costCategoryControls.findIndex(
      (c) => c.max_cost_cents === null,
    )
    const updatedCostCategoryControls = costCategoryControls.map((c, i) => {
      const updated = { ...c }
      // handle low category
      if (i === lowIndex) {
        updated.max_cost_cents = Math.round(formData.lowPriceCost * 100)
      }
      // handle high category
      if (i === highIndex) {
        updated.min_cost_cents = Math.round(formData.highPriceCost * 100)
      }
      // handle medium category
      if (i === lowIndex + 1 && i !== highIndex) {
        updated.min_cost_cents = Math.round(formData.lowPriceCost * 100)
        updated.max_cost_cents = Math.round(formData.highPriceCost * 100)
      }
      return updated
    })

    const result = await updateCostCategoryControls(
      thermostatId,
      updatedCostCategoryControls,
    )

    if (result.isErr()) {
      return
    }
    navigate.push(`/devices/thermostat/${thermostatId}/cost-categories`)
  }

  return {
    costCategories,
    isLoading: isLoadingOrIdle,
    refetch,
    submitUpdateForm,
  }
}
